import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import LayoutWithAccountCard from '../../../../layout/Layout/layout-account-card'
import { useSelector, useDispatch } from 'react-redux'
import BeneficiaryTransferService from '../../../../services/beneficiary-transfers.service'
import { Row, Col } from 'react-bootstrap'
import Loading from 'custom/assets/loaders/Loading'
import '../../../../styles/beneficiary.css'
import PlusIcon from 'custom/assets/common-icons/plus.svg'
import BeneficiaryDropTarget from '../../../../components/BeneficiaryDropTarget/BeneficiaryDropTarget'
import BeneficiaryGridItem from '../../../../components/BeneficiaryGridItem/BeneficiaryGridItem'
import { carouselCurrentItemSelector, accountsSelector } from '../../../../selectors'
import { useTranslation } from 'react-i18next'
import { BENEFICIARY_TRANSFER_KEYS } from '../../../../utils/translation-constants'
import envConfig from 'custom/config/environment'

export default function BeneficiaryTransfer() {
  const history = useHistory()
  const [stage, setStage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [internalBeneList, setInternalBeneList] = useState([])
  const [otherBeneList, setOtherBeneList] = useState([])
  const [internationalBeneList, setInternationalBeneList] = useState([])
  const accounts = useSelector(accountsSelector) || []
  const accountDetails = useSelector(carouselCurrentItemSelector) || {}
  const [ownAccountList, setOwnAccountList] = useState([])
  const [regionalList, setRegionalList] = useState([])
  const [searchTerm, setSearchTerm] = useState([])
  const [listOfBen, setListOfBen] = useState()
  const { t } = useTranslation()

  const TABS = {
    INTERNAL: {
      label: t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.WITH_IN_BANK),
      stage: 1,
    },
    DOMESTIC: {
      label: t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.DOMESTIC),
      stage: 2,
    },
    INTERNATIONAL: {
      label: t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.INTERNATIONAL),
      stage: 3,
    },
    OWN: {
      label: t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.MY_ACCOUNT),
      stage: 4,
    },
    REGION: {
      label: t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.REGIONAL),
      stage: 5,
    },
  }

  useEffect(() => {
    fetchOwnAccounts()
  }, [accountDetails])

  useEffect(() => {
    fetchBeneficiaries()
    fetchOwnAccounts()
  }, [])

  const fetchOwnAccounts = useCallback(async () => {
    try {
      const accountListCopy = [
        ...accounts.filter((acc) => acc?.accountNo !== accountDetails?.accountNo),
      ]
      setOwnAccountList(accountListCopy)
    } catch (err) {
      setLoading(false)
    }
  }, [accounts, accountDetails])

  const fetchBeneficiaries = useCallback(async () => {
    try {
      setLoading(true)
      const response = await BeneficiaryTransferService.getAllBeneficiaries()
      if (response.status === 200) {
        setListOfBen(response.data)
        setInternalBeneList(response.data['internalBeneficiaries'])
        setInternationalBeneList(
          response.data['thirdPartyBeneficiaries'].filter(
            (ele) => ele.beneType === 'INTERNATIONAL',
          ),
        )
        setOtherBeneList(
          response.data['thirdPartyBeneficiaries'].filter((ele) => ele.beneType === 'THIRD_PARTY'),
        )
        setRegionalList(
          response.data['thirdPartyBeneficiaries'].filter((ele) => ele.beneType === 'REGION'),
        )
      }
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }, [])

  const handleSearchChange = useCallback(
    (e) => {
      setSearchTerm(e.target.value)
      if (stage === 1) {
        if (e.target.value === '') {
          setInternalBeneList(listOfBen.internalBeneficiaries)
        } else
          setInternalBeneList(
            listOfBen.internalBeneficiaries.filter((v) => {
              return v.shortName.toLowerCase().includes(e.target.value.toLowerCase())
            }),
          )
      } else if (stage === 2) {
        if (e.target.value === '') {
          setOtherBeneList(
            listOfBen.thirdPartyBeneficiaries.filter((ele) => ele.beneType === 'THIRD_PARTY'),
          )
        } else
          setOtherBeneList(
            listOfBen.thirdPartyBeneficiaries
              .filter((ele) => ele.beneType === 'THIRD_PARTY')
              .filter((v) => {
                return v.shortName.toLowerCase().includes(e.target.value.toLowerCase())
              }),
          )
      } else if (stage === 3) {
        if (e.target.value === '') {
          setInternationalBeneList(
            listOfBen.thirdPartyBeneficiaries.filter((ele) => ele.beneType === 'INTERNATIONAL'),
          )
        } else
          setInternationalBeneList(
            listOfBen.thirdPartyBeneficiaries
              .filter((ele) => ele.beneType === 'INTERNATIONAL')
              .filter((v) => {
                return v.shortName.toLowerCase().includes(e.target.value.toLowerCase())
              }),
          )
      } else if (stage === 4) {
        if (e.target.value === '') {
          setOwnAccountList(ownAccountList)
        } else
          setOwnAccountList(
            ownAccountList.filter((v) => {
              return v.accountNo.toLowerCase().includes(e.target.value.toLowerCase())
            }),
          )
      }
      else if (stage === 5) {
        if (e.target.value === '') {
          setRegionalList(
            listOfBen.thirdPartyBeneficiaries.filter((ele) => ele.beneType === 'REGION'),
          )
        } else
          setRegionalList(
            listOfBen.thirdPartyBeneficiaries
              .filter((ele) => ele.beneType === 'REGION')
              .filter((v) => {
                return v.shortName.toLowerCase().includes(e.target.value.toLowerCase())
              }),
          )
      }
    },
    [stage, listOfBen],
  )

  useEffect(() => {
    setSearchTerm('')
  }, [stage])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return internalBeneList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='IAT'
            linkPath='/beneficiary-transfers/transfers/internal'
            key={i}
            beneDetails={x}
          />
        ))
      case 2:
        return otherBeneList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='OTHER'
            linkPath='/beneficiary-transfers/transfers/other'
            key={i}
            beneDetails={x}
          />
        ))
      case 3:
        return internationalBeneList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='OTHER'
            linkPath='/beneficiary-transfers/transfers/international'
            key={i}
            beneDetails={x}
          />
        ))

      case 4:
        return ownAccountList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='OAT'
            linkPath='/beneficiary-transfers/transfers/own'
            key={i}
            beneDetails={x}
          />
        ))

      case 5:
        return regionalList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='REGION'
            linkPath='/beneficiary-transfers/transfers/regional'
            key={i}
            beneDetails={x}
          />
        ))
      default:
        return internalBeneList.map((x, i) => (
          <BeneficiaryGridItem
            transferType='IAT'
            linkPath='/beneficiary-transfers/transfers/internal'
            key={i}
            beneDetails={x}
          />
        ))
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderGridView() {
    return (
      <>
        <DndProvider backend={HTML5Backend}>
          <div className='row'>
            <div className='col-6'>
              <Row>
                {stage === 1 && (
                  <Col
                    xs={'auto'}
                    sm={'auto'}
                    md={'auto'}
                    onClick={() => history.push('/beneficiary-transfers/add-beneficiary/internal')}
                    className='bene-card'>
                    <img width={30} height={30} src={PlusIcon} />
                    <span style={{ color: 'var(--secondary_1)' }}>
                      {t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.ADD_NEW)}
                    </span>
                  </Col>
                )}
                {stage === 2 && (
                  <Col
                    xs={'auto'}
                    sm={'auto'}
                    md={'auto'}
                    onClick={() => history.push('/beneficiary-transfers/add-beneficiary/domestic')}
                    className='bene-card'>
                    <img width={30} height={30} src={PlusIcon} />
                    <span style={{ color: 'var(--secondary_1)' }}>
                      {t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.ADD_NEW)}
                    </span>
                  </Col>
                )}
                {stage === 4 && ownAccountList.length === 0 && (
                  <div style={{ margin: '0 auto', marginTop: 30 }} className='no-data-found'>
                    {t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.NO_ACCOUNT)}
                  </div>
                )}
                {renderSwitch().slice(0, 5)}
              </Row>
            </div>
            <div className='col-6'>
              <BeneficiaryDropTarget />
            </div>
            <Row className='m-0'>
              {renderSwitch().slice(5)}
            </Row>
          </div>
        </DndProvider>
      </>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_TRANSFER'>
      <div className='view-beneficiary-list'>
        <h4>{t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.BENE_TRANSFER_TITLE)}</h4>
        <div className='row bene-navbar d-flex justify-content-between'>
          {envConfig.BENEFICIARY_TRANSFER_TABS.map((option, index) => (
            <div
              key={index}
              className={
                stage === TABS[option]['stage']
                  ? `col-${12 / envConfig.BENEFICIARY_TRANSFER_TABS.length} selected`
                  : `col-${12 / envConfig.BENEFICIARY_TRANSFER_TABS.length}`
              }>
              {console.log('option', option)}
              <span onClick={() => setStage(TABS[option]['stage'])}>{TABS[option]['label']}</span>
            </div>
          ))}
        </div>
        <div className='mb-3'>
          <input
            className=' col-12 search-box'
            name='search'
            placeholder={t(BENEFICIARY_TRANSFER_KEYS.BENEFICIARY_TRANSFER.SEARCH)}
            type='search'
            onChange={handleSearchChange}
            value={searchTerm}
          />
        </div>
      </div>
      {loading && renderLoading()}
      <div style={{ position: 'relative' }}>{!loading && renderGridView()}</div>
    </LayoutWithAccountCard>
  )
}
